import { useEffect, useState } from "react";
import api from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Truck, Plus, MapPin, ArrowRight, Mail, Edit3, Trash2, Save, X, Search } from "lucide-react";
import CourierSelect from "@/components/CourierSelect";
import ParcelTypeSelect from "@/components/ParcelTypeSelect";
import PincodeInput from "@/components/PincodeInput";
import EmptyState from "@/components/EmptyState";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { useAuth } from "@/context/AuthContext";
import ServerPagination from "@/components/ServerPagination";

const emptyForm = {
  courier: "Delhivery", courier_email: "", direction: "from_company",
  pickup_name: "", pickup_phone: "", pickup_address: "", pickup_city: "", pickup_pincode: "",
  drop_name: "", drop_phone: "", drop_address: "", drop_city: "", drop_pincode: "",
  scheduled_at: "", weight_kg: 0.5, item_count: 1, parcel_type: "Parcel", notes: "",
};
const LSKEY = "pickups.pageSize";
const LSFILTER = "pickups.filters";

export default function PickupsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(parseInt(localStorage.getItem(LSKEY) || "50", 10));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const { user } = useAuth();
  const canDelete = user && ["branch_user", "branch_admin", "company_admin", "super_admin"].includes(user.role);

  // Filters (persisted in localStorage)
  const savedFilters = (() => { try { return JSON.parse(localStorage.getItem(LSFILTER) || "{}"); } catch { return {}; } })();
  const [q, setQ] = useState(savedFilters.q || "");
  const [fStatus, setFStatus] = useState(savedFilters.fStatus || "");
  const [fCourier, setFCourier] = useState(savedFilters.fCourier || "");
  const [fStart, setFStart] = useState(savedFilters.fStart || "");
  const [fEnd, setFEnd] = useState(savedFilters.fEnd || "");

  const load = async (nextPage = page, nextSize = pageSize) => {
    const params = { page: nextPage, page_size: nextSize };
    if (q) params.q = q;
    if (fStatus) params.status = fStatus;
    if (fCourier) params.courier = fCourier;
    if (fStart) params.start = fStart;
    if (fEnd) params.end = fEnd;
    try {
      const { data } = await api.get("/pickups", { params });
      setItems(data.items || []);
      setTotal(data.total || 0); setPages(data.pages || 1);
    } catch (_e) { /* silent */ }
  };
  useEffect(() => { load(1, pageSize); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(page, pageSize); /* eslint-disable-next-line */ }, [page, pageSize]);
  const applyFilters = () => {
    localStorage.setItem(LSFILTER, JSON.stringify({ q, fStatus, fCourier, fStart, fEnd }));
    setPage(1); load(1, pageSize);
  };
  const resetFilters = () => {
    setQ(""); setFStatus(""); setFCourier(""); setFStart(""); setFEnd("");
    localStorage.removeItem(LSFILTER); setPage(1); setTimeout(() => load(1, pageSize), 0);
  };

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pickup_name || !form.pickup_address || !form.drop_name || !form.drop_address) {
      return toast.error("Pickup and drop addresses required");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/pickups", form);
      toast.success(`Pickup ${data.reference} created${data.email_sent ? " · email sent" : ""}`);
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status, ref) => {
    // New pickup status endpoint: cascades to booking when status = picked_up
    try {
      await api.patch(`/pickups/${id}/status`, { pickup_status: status });
      toast.success(`Pickup ${ref} → ${status.replaceAll("_", " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditForm({
      courier: p.courier || "",
      courier_email: p.courier_email || "",
      pickup_name: p.pickup_name || "", pickup_phone: p.pickup_phone || "",
      pickup_address: p.pickup_address || "", pickup_city: p.pickup_city || "", pickup_pincode: p.pickup_pincode || "",
      drop_name: p.drop_name || "", drop_phone: p.drop_phone || "",
      drop_address: p.drop_address || "", drop_city: p.drop_city || "", drop_pincode: p.drop_pincode || "",
      scheduled_at: p.scheduled_at || "", weight_kg: p.weight_kg ?? 0.5,
      item_count: p.item_count ?? 1, parcel_type: p.parcel_type || "Parcel", notes: p.notes || "",
    });
  };
  const saveEdit = async () => {
    try { await api.patch(`/pickups/${editingId}`, editForm); toast.success("Pickup updated"); setEditingId(null); setEditForm(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removePickup = (p) => setPendingDelete(p);
  const confirmDeletePickup = async (reason) => {
    const target = pendingDelete;
    if (!target) return false;
    try {
      await api.delete(`/pickups/${target.id}`, { params: { reason: reason || "" } });
      toast.success("Pickup deleted");
      setPendingDelete(null);
      load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      return false;
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><Truck size={12}/> Courier pickup orchestration</div>
          <h1 className="text-4xl font-display mt-1">Pickups.</h1>
          <p className="text-sm text-muted-foreground mt-1">Send a pickup request to any courier — inbound to your HQ / branch, or outbound to any PAN India location.</p>
        </div>
        <Button data-testid="pickup-new-btn" onClick={()=>setOpen(true)} className="rounded-md bg-primary text-primary-foreground shadow-brut-orange">
          <Plus size={14} className="mr-2"/> New pickup
        </Button>
      </header>

      <div className="border border-border rounded-md bg-card overflow-x-auto card-elev">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left overline">
              <th className="p-4">Ref</th><th className="p-4">Courier</th><th className="p-4">Direction</th>
              <th className="p-4">From → To</th><th className="p-4">Status</th><th className="p-4">Created</th><th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="pickups-table">
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-0">
                <EmptyState icon={Truck} variant="package" title="No pickups yet"
                  subtitle="Create your first pickup request." testid="empty-pickups"
                  action={<Button onClick={()=>setOpen(true)} className="rounded-md bg-primary text-primary-foreground shadow-brut-orange"><Plus size={14} className="mr-2"/>New pickup</Button>}
                />
              </td></tr>
            )}
            {items.map(p => (
              <tr key={p.id} className="border-b border-border hover:bg-muted/40">
                <td className="p-4 mono text-xs">{p.reference}</td>
                <td className="p-4 mono">{p.courier}</td>
                <td className="p-4">
                  <span className="text-[10px] uppercase tracking-widest border border-border px-2 py-0.5">
                    {p.direction === "to_company" ? "→ Company" : "Company →"}
                  </span>
                </td>
                <td className="p-4 text-xs">
                  <div>{p.pickup_city || p.pickup_name} {p.pickup_pincode || ""}</div>
                  <div className="flex items-center gap-1 text-muted-foreground"><ArrowRight size={10}/>{p.drop_city || p.drop_name} {p.drop_pincode || ""}</div>
                </td>
                <td className="p-4">
                  <Select value={p.pickup_status || p.status || "pickup_requested"} onValueChange={(v)=>setStatus(p.id, v, p.reference)}>
                    <SelectTrigger data-testid={`pickup-status-${p.reference}`} className="h-7 rounded-md text-xs w-40"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["pickup_requested","pending_pickup","pickup_delayed","picked_up","cancelled"].map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4 text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</td>
                <td className="p-4 text-xs">{p.email_sent && <span className="text-primary inline-flex items-center gap-1"><Mail size={11}/>sent</span>}</td>
                <td className="p-4 text-right whitespace-nowrap">
                  <button
                    type="button"
                    data-testid={`pickup-edit-${p.reference}`}
                    onClick={() => startEdit(p)}
                    className="text-muted-foreground hover:text-primary p-1.5 rounded transition-colors inline-flex items-center"
                    title="Edit pickup"
                  ><Edit3 size={14} /></button>
                  {canDelete && !["picked_up", "in_transit", "delivered"].includes(p.status) && (
                    <button
                      type="button"
                      data-testid={`pickup-delete-${p.reference}`}
                      onClick={() => removePickup(p)}
                      className="ml-1 text-muted-foreground hover:text-primary p-1.5 rounded transition-colors inline-flex items-center"
                      title="Delete pickup"
                    ><Trash2 size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ServerPagination
        page={page} pageSize={pageSize} total={total} pages={pages}
        onPageChange={(n) => setPage(n)}
        onPageSizeChange={(s) => { setPageSize(s); localStorage.setItem(LSKEY, String(s)); setPage(1); }}
        storageKey={LSKEY} testidPrefix="pickups-pg"
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-md max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">New pickup request</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="pickup-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="overline">Direction</span>
                <Select value={form.direction} onValueChange={(v)=>set("direction",v)}>
                  <SelectTrigger data-testid="pickup-dir" className="rounded-md mt-1"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="to_company">Inbound → Company (pickup from vendor / any PAN India)</SelectItem>
                    <SelectItem value="from_company">Outbound ← Company (pickup from HO/branch, deliver anywhere)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <span className="overline">Courier</span>
                <CourierSelect value={form.courier} onChange={(v)=>set("courier",v)} testid="pickup-courier" />
              </div>
            </div>
            <div>
              <span className="overline">Courier notification email</span>
              <Input data-testid="pickup-courier-email" type="email" value={form.courier_email}
                onChange={(e)=>set("courier_email",e.target.value)} placeholder="pickup@delhivery.com" className="rounded-md mt-1"/>
            </div>

            <div className="grid grid-cols-2 gap-4 border-t border-border pt-4">
              <div className="space-y-2">
                <div className="overline flex items-center gap-1"><MapPin size={11}/> Pickup from *</div>
                <Input data-testid="pu-name" required value={form.pickup_name} onChange={(e)=>set("pickup_name",e.target.value)} placeholder="Contact name" className="rounded-md"/>
                <Input data-testid="pu-phone" value={form.pickup_phone} onChange={(e)=>set("pickup_phone",e.target.value)} placeholder="Phone" className="rounded-md font-mono"/>
                <Textarea data-testid="pu-address" required value={form.pickup_address} onChange={(e)=>set("pickup_address",e.target.value)} placeholder="Full address" rows={2} className="rounded-md"/>
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="pu-city" value={form.pickup_city} onChange={(e)=>set("pickup_city",e.target.value)} placeholder="City" className="rounded-md"/>
                  <PincodeInput value={form.pickup_pincode} onChange={(v)=>set("pickup_pincode",v)}
                    onResolved={(i)=>{ if(!form.pickup_city && i?.city) set("pickup_city", i.city); }}
                    testid="pu-pincode" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="overline flex items-center gap-1"><MapPin size={11} className="text-primary"/> Deliver to *</div>
                <Input data-testid="dr-name" required value={form.drop_name} onChange={(e)=>set("drop_name",e.target.value)} placeholder="Contact name" className="rounded-md"/>
                <Input data-testid="dr-phone" value={form.drop_phone} onChange={(e)=>set("drop_phone",e.target.value)} placeholder="Phone" className="rounded-md font-mono"/>
                <Textarea data-testid="dr-address" required value={form.drop_address} onChange={(e)=>set("drop_address",e.target.value)} placeholder="Full address" rows={2} className="rounded-md"/>
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="dr-city" value={form.drop_city} onChange={(e)=>set("drop_city",e.target.value)} placeholder="City" className="rounded-md"/>
                  <PincodeInput value={form.drop_pincode} onChange={(v)=>set("drop_pincode",v)}
                    onResolved={(i)=>{ if(!form.drop_city && i?.city) set("drop_city", i.city); }}
                    testid="dr-pincode" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3 border-t border-border pt-4">
              <Input data-testid="pickup-weight" type="number" step="0.1" value={form.weight_kg}
                onChange={(e)=>set("weight_kg", parseFloat(e.target.value)||0)} className="rounded-md font-mono" placeholder="Weight kg"/>
              <Input data-testid="pickup-items" type="number" step="1" value={form.item_count}
                onChange={(e)=>set("item_count", Math.max(1, parseInt(e.target.value)||1))} className="rounded-md font-mono" placeholder="# items"/>
              <ParcelTypeSelect value={form.parcel_type} onChange={(v)=>set("parcel_type",v)} testid="pickup-type"/>
              <Input data-testid="pickup-when" type="datetime-local" value={form.scheduled_at}
                onChange={(e)=>set("scheduled_at",e.target.value)} className="rounded-md"/>
            </div>
            <Input data-testid="pickup-notes" value={form.notes} onChange={(e)=>set("notes",e.target.value)} placeholder="Notes for courier" className="rounded-md"/>

            <Button type="submit" data-testid="pickup-submit" disabled={busy}
              className="w-full rounded-md bg-primary text-primary-foreground shadow-brut-orange h-11">
              {busy ? "Sending…" : "Send pickup request →"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editingId} onOpenChange={(o) => { if (!o) { setEditingId(null); setEditForm(null); } }}>
        <DialogContent className="rounded-md max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pickup-edit-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Edit pickup</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-4" data-testid="pickup-edit-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="overline">Courier</span>
                  <CourierSelect value={editForm.courier} onChange={(v)=>setEditForm({...editForm, courier:v})} testid="epk-courier" />
                </div>
                <div>
                  <span className="overline">Courier email</span>
                  <Input type="email" data-testid="epk-courier-email" value={editForm.courier_email}
                    onChange={(e)=>setEditForm({...editForm, courier_email:e.target.value})} className="rounded-md mt-1" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 border-t border-border pt-3">
                <div className="space-y-2">
                  <div className="overline">Pickup from</div>
                  <Input data-testid="epk-pu-name" value={editForm.pickup_name} onChange={(e)=>setEditForm({...editForm, pickup_name:e.target.value})} placeholder="Name" className="rounded-md"/>
                  <Input data-testid="epk-pu-phone" value={editForm.pickup_phone} onChange={(e)=>setEditForm({...editForm, pickup_phone:e.target.value})} placeholder="Phone" className="rounded-md font-mono"/>
                  <Textarea data-testid="epk-pu-address" value={editForm.pickup_address} onChange={(e)=>setEditForm({...editForm, pickup_address:e.target.value})} rows={2} className="rounded-md"/>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="epk-pu-city" value={editForm.pickup_city} onChange={(e)=>setEditForm({...editForm, pickup_city:e.target.value})} placeholder="City" className="rounded-md"/>
                    <Input data-testid="epk-pu-pincode" value={editForm.pickup_pincode} onChange={(e)=>setEditForm({...editForm, pickup_pincode:e.target.value})} placeholder="Pincode" className="rounded-md font-mono"/>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="overline">Deliver to</div>
                  <Input data-testid="epk-dr-name" value={editForm.drop_name} onChange={(e)=>setEditForm({...editForm, drop_name:e.target.value})} placeholder="Name" className="rounded-md"/>
                  <Input data-testid="epk-dr-phone" value={editForm.drop_phone} onChange={(e)=>setEditForm({...editForm, drop_phone:e.target.value})} placeholder="Phone" className="rounded-md font-mono"/>
                  <Textarea data-testid="epk-dr-address" value={editForm.drop_address} onChange={(e)=>setEditForm({...editForm, drop_address:e.target.value})} rows={2} className="rounded-md"/>
                  <div className="grid grid-cols-2 gap-2">
                    <Input data-testid="epk-dr-city" value={editForm.drop_city} onChange={(e)=>setEditForm({...editForm, drop_city:e.target.value})} placeholder="City" className="rounded-md"/>
                    <Input data-testid="epk-dr-pincode" value={editForm.drop_pincode} onChange={(e)=>setEditForm({...editForm, drop_pincode:e.target.value})} placeholder="Pincode" className="rounded-md font-mono"/>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 border-t border-border pt-3">
                <Input data-testid="epk-weight" type="number" step="0.1" value={editForm.weight_kg} onChange={(e)=>setEditForm({...editForm, weight_kg: parseFloat(e.target.value)||0})} className="rounded-md font-mono" placeholder="Weight kg"/>
                <Input data-testid="epk-items" type="number" step="1" value={editForm.item_count} onChange={(e)=>setEditForm({...editForm, item_count: Math.max(1, parseInt(e.target.value)||1)})} className="rounded-md font-mono" placeholder="# items"/>
                <ParcelTypeSelect value={editForm.parcel_type} onChange={(v)=>setEditForm({...editForm, parcel_type:v})} testid="epk-type"/>
                <Input data-testid="epk-when" type="datetime-local" value={editForm.scheduled_at || ""} onChange={(e)=>setEditForm({...editForm, scheduled_at:e.target.value})} className="rounded-md"/>
              </div>
              <Input data-testid="epk-notes" value={editForm.notes} onChange={(e)=>setEditForm({...editForm, notes:e.target.value})} placeholder="Notes" className="rounded-md"/>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={()=>{ setEditingId(null); setEditForm(null); }} className="rounded-md" data-testid="epk-cancel">
                  <X size={14} className="mr-1.5"/> Cancel
                </Button>
                <Button type="button" onClick={saveEdit} className="rounded-md bg-primary text-primary-foreground" data-testid="epk-save">
                  <Save size={14} className="mr-1.5"/> Save changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete pickup"
        subject={pendingDelete?.reference}
        description="Cancel and remove this pickup record. The action is recorded in the audit log."
        onConfirm={confirmDeletePickup}
      />
    </div>
  );
}
